/**
 * The assistant's bubble, bottom-right on every screen — like a live-chat
 * widget. Opens the panel; Esc or the × closes it. On a phone the panel takes
 * the whole screen and the bubble sits above the tab bar.
 */
import { MessageCircle, X } from 'lucide-react'
import { lazy, Suspense, useEffect, useState } from 'react'
import { Button } from '@/components/ui/button'
import { useIsMobile } from '@/lib/hooks'
import { cn } from '@/lib/utils'

// The panel, and the Markdown its replies are drawn with, load the first time
// someone reaches for the bubble — not on every page. Hovering or focusing the
// bubble starts it, so it is there by the time the click lands.
const loadPanel = () => import('./assistant-panel')
const AssistantPanel = lazy(() => loadPanel().then((m) => ({ default: m.AssistantPanel })))

export function AssistantLauncher() {
  const [open, setOpen] = useState(false)
  const isMobile = useIsMobile()

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false)
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  // Above the phone tab bar (56px + the safe area), or 24px from the corner.
  const corner = isMobile
    ? { bottom: 'calc(4.5rem + env(safe-area-inset-bottom))', right: '1rem' }
    : { bottom: '1.5rem', right: '1.5rem' }

  return (
    <>
      {open ? (
        <div
          data-slot="assistant-panel"
          className={cn(
            'fixed z-40',
            isMobile ? 'inset-0' : 'h-[min(640px,calc(100dvh-8rem))] w-[400px]',
          )}
          style={isMobile ? undefined : { bottom: '6rem', right: '1.5rem' }}
        >
          <Suspense fallback={null}>
            <AssistantPanel onClose={() => setOpen(false)} />
          </Suspense>
        </div>
      ) : null}
      {isMobile && open ? null : (
        <Button
          data-slot="assistant-launcher"
          size="icon"
          aria-label={open ? 'Close the assistant' : 'Open the assistant'}
          aria-expanded={open}
          onClick={() => setOpen((was) => !was)}
          onPointerEnter={() => void loadPanel()}
          onFocus={() => void loadPanel()}
          className="fixed z-40 size-14 rounded-full shadow-raised [&_svg]:size-6"
          style={corner}
        >
          {open ? <X /> : <MessageCircle />}
        </Button>
      )}
    </>
  )
}
